// src/App.jsx — Sprint 4c update
// Adds: LandingPage shown to unauthenticated users
//       Sign In / Create Account buttons on landing page trigger Amplify auth modal
// Sprint 4b: email label on auth screens, first-matter payment capture

import { useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { signOut, fetchAuthSession } from 'aws-amplify/auth';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';

import Sidebar           from './components/Sidebar';
import HomeView          from './components/HomeView';
import MatterView        from './components/MatterView';
import AccountPage       from './components/AccountPage';
import LandingPage       from './components/LandingPage';
import { NewMatterModal, FeedbackModal } from './components/Modals';
import Onboarding        from './components/Onboarding';
import { PaymentSuccess, PaymentCancel } from './components/PaymentPages';
import { listMatters, createMatter, submitFeedback, getMe, createCheckoutSession } from './api';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId:       'ap-southeast-2_FDVGJrK8q',
      userPoolClientId: 'ma38541m55rp866v1tdngkrj0',
      region:           'ap-southeast-2',
    }
  }
});

const fontLink = document.createElement('link');
fontLink.rel  = 'stylesheet';
fontLink.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap';
document.head.appendChild(fontLink);

// ── Amplify form field overrides — email label on sign-in and sign-up ─────────
const formFields = {
  signIn: {
    username: {
      label: 'Email',
      placeholder: 'Enter your email',
    },
  },
  signUp: {
    username: {
      label: 'Email',
      placeholder: 'Enter your email',
      order: 1,
    },
    password: {
      order: 2,
    },
    confirm_password: {
      order: 3,
    },
  },
};

// ── Root — wraps everything in Authenticator provider ─────────────────────────
// The Authenticator component here acts as a provider only (no UI rendered at
// this level). The LandingPage is shown to unauthenticated users. Sign In /
// Create Account buttons on the landing page open the Amplify auth modal via
// the useAuthenticator hook in AuthGate below.

export default function App() {
  return (
    <Authenticator.Provider>
      <AuthGate />
    </Authenticator.Provider>
  );
}

// ── AuthGate — shows landing page or dashboard depending on auth state ────────

function AuthGate() {
  const { authStatus, toSignIn, toSignUp } = useAuthenticator(ctx => [ctx.authStatus]);
  const [showAuthModal, setShowAuthModal]  = useState(false);
  const [initialState,  setInitialState]   = useState('signIn');

  const handleSignIn = () => {
    setInitialState('signIn');
    setShowAuthModal(true);
  };

  const handleCreateAccount = () => {
    setInitialState('signUp');
    setShowAuthModal(true);
  };

  // Once authenticated, dismiss the modal gate
  useEffect(() => {
    if (authStatus === 'authenticated') setShowAuthModal(false);
  }, [authStatus]);

  // ── Not yet authenticated — show landing page + optional auth modal ────────
  if (authStatus !== 'authenticated') {
    return (
      <>
        <LandingPage
          onSignIn={handleSignIn}
          onCreateAccount={handleCreateAccount}
        />

        {/* Auth modal — rendered over landing page when user clicks sign in/up */}
        {showAuthModal && (
          <div
            onClick={e => e.target === e.currentTarget && setShowAuthModal(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(13,27,42,0.65)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 2000,
            }}
          >
            <div style={{ position: 'relative', width: '100%', maxWidth: 460 }}>
              <button
                onClick={() => setShowAuthModal(false)}
                style={{
                  position: 'absolute', top: -12, right: -12, zIndex: 10,
                  background: '#FFF', border: 'none', borderRadius: '50%',
                  width: 32, height: 32, fontSize: 18, color: '#888',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >×</button>
              <Authenticator
                hideSignUp={true}
                initialState={initialState}
                formFields={formFields}
                components={{
                  Header: () => (
                    <div style={{ textAlign: 'center', padding: '32px 0 8px' }}>
                      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, color: '#C8A84B' }}>LitFit™</div>
                      <div style={{ fontSize: 11, color: '#BBB', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 4 }}>Calderbank.ai</div>
                    </div>
                  ),
                }}
              >
                {({ user }) => <Dashboard cognitoUser={user} />}
              </Authenticator>
            </div>
          </div>
        )}
      </>
    );
  }

  // ── Authenticated — render dashboard directly ─────────────────────────────
  // useAuthenticator doesn't give us the user object here, so we use a
  // separate Authenticator wrapper just for the dashboard render.
  return (
    <Authenticator
      hideSignUp={true}
      formFields={formFields}
      components={{
        Header: () => (
          <div style={{ textAlign: 'center', padding: '32px 0 8px' }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, color: '#C8A84B' }}>LitFit™</div>
            <div style={{ fontSize: 11, color: '#BBB', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 4 }}>Calderbank.ai</div>
          </div>
        ),
      }}
    >
      {({ user }) => <Dashboard cognitoUser={user} />}
    </Authenticator>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard({ cognitoUser }) {
  const [token,             setToken]           = useState(null);
  const [onboarded,         setOnboarded]       = useState(null);
  const [matters,           setMatters]         = useState([]);
  const [activeMatterId,    setActiveMatterId]  = useState(null);
  const [expectNewRunFor, setExpectNewRunFor] = useState(null);  // A1: bridge flag for fresh-checkout return
  const [showAccount,       setShowAccount]     = useState(false);
  const [showNewMatter,     setShowNewMatter]   = useState(false);
  const [newMatterLoading,  setNewMatterLoading] = useState(false);
  const [feedback,          setFeedback]        = useState(null);
  const [loading,           setLoading]         = useState(true);
  const [hasPaymentMethod,  setHasPaymentMethod] = useState(null);

  const path             = window.location.pathname;
  const isPaymentSuccess = path === '/payment/success';
  const isPaymentCancel  = path === '/payment/cancel';

  // ── Auth token ─────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchAuthSession()
      .then(session => setToken(session.tokens?.idToken?.toString()))
      .catch(console.error);
  }, []);

  // ── Check onboarding + payment method ─────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    getMe(token)
      .then(data => {
        setOnboarded(data.onboarded === true);
        setHasPaymentMethod(data.has_payment_method === true);
      })
      .catch(() => {
        setOnboarded(false);
        setHasPaymentMethod(false);
      });
  }, [token]);

  // ── Load matters ───────────────────────────────────────────────────────────
  const loadMatters = useCallback(async (selectId = null, deleted = false) => {
    if (!token || !onboarded) return;
    try {
      const data = await listMatters(token);
      setMatters(data.matters || []);
      if (deleted) setActiveMatterId(null);
      else if (selectId) setActiveMatterId(selectId);
    } catch (e) {
      console.error('Failed to load matters:', e);
    } finally {
      setLoading(false);
    }
  }, [token, onboarded]);

  useEffect(() => {
    if (onboarded) loadMatters();
    else if (onboarded === false) setLoading(false);
  }, [onboarded, loadMatters]);

  // ── Create matter — with first-matter payment capture ─────────────────────
  const handleCreateMatter = async (name, retentionDays = 30) => {
    setNewMatterLoading(true);
    try {
      if (!hasPaymentMethod) {
        sessionStorage.setItem('pendingMatterName', name);
        sessionStorage.setItem('pendingMatterRetentionDays', String(retentionDays));
        const { checkout_url } = await createCheckoutSession(token, null, null, {}, {
          setup_only: true,
          success_url: `${window.location.origin}/payment/success?setup=true`,
          cancel_url:  `${window.location.origin}/payment/cancel`,
        });
        window.location.href = checkout_url;
        return;
      }
      const res = await createMatter(token, name, retentionDays);
      await loadMatters(res.matter_id);
      setShowNewMatter(false);
    } catch (e) {
      alert(`Failed to create matter: ${e.message}`);
    } finally {
      setNewMatterLoading(false);
    }
  };

  // ── Payment ────────────────────────────────────────────────────────────────
  const handlePayAndRun = async (matterId, perspective, runContext = {}) => {
    try {
      const { checkout_url } = await createCheckoutSession(token, matterId, perspective, runContext);
      window.location.href = checkout_url;
    } catch (e) {
      alert(`Payment setup failed: ${e.message}`);
    }
  };

  // ── Sign out ───────────────────────────────────────────────────────────────
  const handleSignOut = async () => {
    await signOut();
    window.location.reload();
  };

  const activeMatter = matters.find(m => m.matter_id === activeMatterId);
  const userName     = cognitoUser?.signInDetails?.loginId || cognitoUser?.username || '';

  // ── Loading ────────────────────────────────────────────────────────────────
  if (!token || onboarded === null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F7F5F0' }}>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, color: '#C8A84B' }}>Loading…</div>
      </div>
    );
  }

  // ── Payment pages ──────────────────────────────────────────────────────────
  if (isPaymentSuccess) {
    const params    = new URLSearchParams(window.location.search);
    const setupOnly = params.get('setup') === 'true';

    if (setupOnly) {
      const pendingName = sessionStorage.getItem('pendingMatterName');
      const pendingRetentionDays = Number(sessionStorage.getItem('pendingMatterRetentionDays')) || 30;
      sessionStorage.removeItem('pendingMatterName');
      sessionStorage.removeItem('pendingMatterRetentionDays');
      if (pendingName) {
        createMatter(token, pendingName, pendingRetentionDays)
          .then(res => {
            setHasPaymentMethod(true);
            window.history.pushState({}, '', '/');
            loadMatters(res.matter_id);
          })
          .catch(() => {
            window.history.pushState({}, '', '/');
          });
      } else {
        window.history.pushState({}, '', '/');
      }
    }

    return <PaymentSuccess token={token} onGoToMatter={(id) => {
      setExpectNewRunFor(id);
      window.history.pushState({}, '', '/');
      setActiveMatterId(id);
      loadMatters(id);
    }} />;
  }

  if (isPaymentCancel) {
    sessionStorage.removeItem('pendingMatterName');
    return <PaymentCancel onGoToMatter={(id) => {
      window.history.pushState({}, '', '/');
      if (id) setActiveMatterId(id);
    }} />;
  }

  // ── Onboarding gate ────────────────────────────────────────────────────────
  if (!onboarded) {
    return (
      <Onboarding
        token={token}
        onComplete={() => {
          setOnboarded(true);
          loadMatters();
        }}
      />
    );
  }

  // ── Account page ───────────────────────────────────────────────────────────
  if (showAccount) {
    return (
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: "'DM Sans', system-ui, sans-serif", background: '#F7F5F0' }}>
        <Sidebar
          matters={matters}
          activeMatterId={null}
          onSelectMatter={(id) => { setShowAccount(false); setActiveMatterId(id); }}
          onHome={() => { setShowAccount(false); setActiveMatterId(null); }}
          onNewMatter={() => { setShowAccount(false); setShowNewMatter(true); }}
          onAccount={() => setShowAccount(true)}
          onSignOut={handleSignOut}
          user={{ name: userName }}
        />
        <AccountPage
          token={token}
          user={{ name: userName }}
          onBack={() => setShowAccount(false)}
          onSignOut={handleSignOut}
        />
      </div>
    );
  }

  // ── Main dashboard ─────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: "'DM Sans', system-ui, sans-serif", background: '#F7F5F0', color: '#1A1A1A' }}>
      <Sidebar
        matters={matters}
        activeMatterId={activeMatterId}
        onSelectMatter={setActiveMatterId}
        onHome={() => setActiveMatterId(null)}
        onNewMatter={() => setShowNewMatter(true)}
        onAccount={() => setShowAccount(true)}
        onSignOut={handleSignOut}
        user={{ name: userName }}
      />

      <div style={{ flex: 1, height: '100vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {activeMatter ? (
          <MatterView
            key={activeMatter.matter_id}
            matter={activeMatter}
            token={token}
            onMatterUpdate={(selectId, deleted) => loadMatters(selectId || activeMatterId, deleted)}
            onShowFeedback={(runId, score) => setFeedback({ runId, score })}
            expectNewRun={expectNewRunFor === activeMatterId}
            onRunFound={() => setExpectNewRunFor(null)}
            onPayAndRun={handlePayAndRun}
          />
        ) : (
          <HomeView
            matters={matters}
            onNewMatter={() => setShowNewMatter(true)}
            onSelectMatter={setActiveMatterId}
          />
        )}
      </div>

      {showNewMatter && (
        <NewMatterModal
          onConfirm={handleCreateMatter}
          onClose={() => setShowNewMatter(false)}
          loading={newMatterLoading}
        />
      )}

      {feedback && (
        <FeedbackModal
          runId={feedback.runId}
          score={feedback.score}
          onSubmit={async (runId, rating, comment) => {
            try { await submitFeedback(token, runId, rating, comment); } catch {}
          }}
          onClose={() => setFeedback(null)}
        />
      )}
    </div>
  );
}
